import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const GameCard = ({ game, onWhatsAppClick, onPreviewClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount);
  };

  return (
    <div 
      className="bg-card rounded-card border border-border shadow-subtle hover:shadow-royal transition-royal overflow-hidden group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Game Image */}
      <div className="relative h-48 overflow-hidden">
        <Image
          src={game?.image}
          alt={game?.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-royal"
        />
        
        {/* Live Indicator */}
        {game?.isLive && (
          <div className="absolute top-3 left-3 flex items-center space-x-1 bg-error/90 text-error-foreground px-2 py-1 rounded-button text-xs font-medium">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
            <span>LIVE</span>
          </div>
        )}

        {/* Popular Badge */}
        {game?.isPopular && (
          <div className="absolute top-3 right-3 bg-primary/90 text-primary-foreground px-2 py-1 rounded-button text-xs font-medium">
            <Icon name="TrendingUp" size={12} className="inline mr-1" />
            Popular
          </div>
        )}

        {/* Preview Button */}
        <div className={`absolute inset-0 bg-black/50 flex items-center justify-center transition-royal ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onPreviewClick(game)}
            iconName="Play"
            iconPosition="left"
            className="bg-white/90 text-black hover:bg-white"
          >
            Preview Game
          </Button>
        </div>

        {/* Player Count */}
        {game?.playerCount && (
          <div className="absolute bottom-3 left-3 bg-black/70 text-white px-2 py-1 rounded-button text-xs">
            <Icon name="Users" size={12} className="inline mr-1" />
            {game?.playerCount} playing
          </div>
        )}
      </div>
      {/* Game Details */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-heading font-semibold text-lg text-text-primary mb-1">
              {game?.name}
            </h3>
            <p className="text-sm text-text-secondary">
              {game?.category}
            </p>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Star" size={16} color="var(--color-warning)" fill="var(--color-warning)" />
            <span className="text-sm font-medium text-text-primary">{game?.rating}</span>
          </div>
        </div>

        {/* Game Description */}
        <p className="text-sm text-text-secondary mb-3 line-clamp-2">
          {game?.description}
        </p>

        {/* Game Stats */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className="text-center">
              <p className="text-xs text-text-muted">Min Bet</p>
              <p className="text-sm font-semibold text-primary">
                {formatAmount(game?.minBet)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-text-muted">Max Win</p>
              <p className="text-sm font-semibold text-success">
                {formatAmount(game?.maxWin)}
              </p>
            </div>
          </div>
          
          {game?.recentWinner && (
            <div className="text-right">
              <p className="text-xs text-text-muted">Recent Win</p>
              <p className="text-sm font-semibold text-success">
                {formatAmount(game?.recentWinner?.amount)}
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-2">
          <Button
            variant="default"
            size="sm"
            onClick={() => onWhatsAppClick(game)}
            iconName="MessageCircle"
            iconPosition="left"
            className="flex-1 bg-gradient-royal hover:shadow-glow"
          >
            Play via WhatsApp
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPreviewClick(game)}
            iconName="Info"
            className="px-3"
          >
          </Button>
        </div>

        {/* Game Features */}
        {game?.features && game?.features?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {game?.features?.map((feature, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded-button bg-muted text-text-secondary text-xs"
              >
                {feature}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GameCard;